import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet } from '../lib/api';
import type { TicketsResponse } from '../types';
import { TicketRow } from '../components/TicketRow';
import { TicketListControls } from '../components/TicketListControls';
import { TicketListHeader } from '../components/TicketListHeader';
import { useRefs } from '../context/RefsContext';

const PRESET_TITLE: Record<string, string> = {
  a_traiter: 'À traiter',
  mes: 'Mes points',
  urgents: 'Urgents (P1-P3)',
  attente_client: 'En attente client',
  a_mettre_prod: 'À mettre en prod',
  archives: 'Archives',
};

export function TicketsListPage() {
  const [params, setSearchParams] = useSearchParams();
  const { clients } = useRefs();
  // Filtres et tri : remplacent l'entrée d'historique (pas un « retour » par lettre tapée).
  const setParams = useCallback((next: URLSearchParams) => setSearchParams(next, { replace: true }), [setSearchParams]);
  const [data, setData] = useState<TicketsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const ctl = new AbortController();
    setLoading(true);
    setErr(null);
    const qs = params.toString();
    apiGet<TicketsResponse>(`/api/tickets${qs ? '?' + qs : ''}`, ctl.signal)
      .then((d) => {
        if (!ctl.signal.aborted) setData(d);
      })
      .catch((e) => {
        if (ctl.signal.aborted) return;
        setErr(e instanceof Error ? e.message : 'Erreur');
      })
      .finally(() => {
        if (!ctl.signal.aborted) setLoading(false);
      });
    return () => ctl.abort();
  }, [params]);

  const preset = params.get('preset') ?? '';
  const title = PRESET_TITLE[preset] ?? 'Tous les points';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <h1 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, margin: 0 }}>
          {/* Total masqué pendant le chargement : sinon celui du filtre précédent
              s'affiche un instant sous le nouveau titre. */}
          {title} {!loading && data && <span style={{ color: 'var(--text-mute)', fontSize: 'var(--fs-base)' }}>· {data.total}</span>}
        </h1>
        <div style={{ flex: 1 }} />
      </div>

      {err && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--danger-soft)',
            border: '1px solid var(--danger-border)',
            color: 'var(--danger)',
            borderRadius: 6,
            marginBottom: 14,
            fontSize: 'var(--fs-sm)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span>{err}</span>
          <button
            onClick={() => window.location.reload()}
            className="btn"
            style={{ fontSize: 'var(--fs-xs)', padding: '4px 12px', borderColor: 'var(--danger-border)', color: 'var(--danger)' }}
          >
            Réessayer
          </button>
        </div>
      )}

      <TicketListControls params={params} setParams={setParams} clients={clients} />

      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <TicketListHeader params={params} setParams={setParams} />
        {loading && <div style={{ padding: 20, color: 'var(--text-mute)' }}>Chargement…</div>}
        {!loading && data && data.items.length === 0 && (
          <div style={{ padding: 20, color: 'var(--text-mute)' }}>Aucun point ne correspond à ce filtre.</div>
        )}
        {!loading &&
          data?.items.map((t, i) => (
            <Link
              key={t.display_id}
              to={`/tickets/${t.display_id}`}
              style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
            >
              <TicketRow t={t} alt={i % 2 === 1} />
            </Link>
          ))}
      </div>
    </div>
  );
}
